import { useState, useEffect } from 'react';
import { isEqual } from 'lodash';
import { ICandidateSourcingFilters, FilterKey, YearsOfExperienceRange } from '../types';
import { addFilterValue, removeFilterValue, sanitizeYearsOfExperience } from './filterUtils';

interface UseFilterStateProps {
  filters: ICandidateSourcingFilters;
  isOpen: boolean;
}

const normalizeFilters = (filters: ICandidateSourcingFilters) => {
  const normalizedFilters = {...filters};
  const sanitizedYears = sanitizeYearsOfExperience(filters?.years_of_experience);
  if (sanitizedYears) {
    normalizedFilters.years_of_experience = sanitizedYears;
  } else {
    delete normalizedFilters.years_of_experience;
  }
  return normalizedFilters;
};

export const useFilterState = ({ filters, isOpen }: UseFilterStateProps) => {
  const [localFilters, setLocalFilters] = useState<ICandidateSourcingFilters>(normalizeFilters(filters));
  const [newValues, setNewValues] = useState<Record<string, string>>({});
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setLocalFilters(normalizeFilters(filters));
      setNewValues({});
      setHasChanges(false);
    }
  }, [isOpen, filters]);

  useEffect(() => {
    const hasChangesMade = isEqual(localFilters, filters);
    setHasChanges(!hasChangesMade);
  }, [localFilters, filters]);

  const handleAddFilter = (key: FilterKey, value: string) => {
    if (!value.trim()) return;

    const updatedFilters = addFilterValue(localFilters, key, value);
    setLocalFilters(updatedFilters);
    setNewValues({ ...newValues, [key]: '' });
  };

  const handleRemoveFilter = (key: FilterKey, index?: number, subIndex?: number) => {
    const updatedFilters = removeFilterValue(localFilters, key, index, subIndex);
    setLocalFilters(updatedFilters);
  };

  const handleYearsOfExperienceChange = (value: YearsOfExperienceRange) => {
    const sanitized = sanitizeYearsOfExperience(value);
    const updatedFilters = { ...localFilters };
    if (sanitized) {
      updatedFilters.years_of_experience = sanitized;
    } else {
      delete updatedFilters.years_of_experience;
    }
    setLocalFilters(updatedFilters);
  };

  const handleYearsOfExperienceClear = () => {
    const updatedFilters = { ...localFilters };
    delete updatedFilters.years_of_experience;
    setLocalFilters(updatedFilters);
  };

  const handleValueChange = (key: FilterKey, value: string) => {
    setNewValues({ ...newValues, [key]: value });
  };

  const resetState = () => {
    setLocalFilters(filters);
    setNewValues({});
  };

  return {
    localFilters,
    newValues,
    hasChanges,
    handleAddFilter,
    handleRemoveFilter,
    handleYearsOfExperienceChange,
    handleYearsOfExperienceClear,
    handleValueChange,
    resetState
  };
};
